import type * as maplibregl from "maplibre-gl";
import { RENDER_STATS_WINDOW_MS, RenderStatsWindow } from "../../core/renderStats";
import type { FrameTimings } from "../../core/renderStats";
import type { DecorRenderStats } from "./decorLayer";

const IDLE_GAP_MS = 1000;

export interface MapRenderStats {
  /** Intervalle entre deux images rendues d'affilée, décor compris : ce que l'on ressent comme fluidité ou à-coups. */
  map: { timings: FrameTimings; counts: Record<string, number> } | null;
  /** Tâches de plus de 50 ms sur la dernière fenêtre : ce sont elles que l'on ressent comme des à-coups. */
  longTasks: { count: number; totalMs: number; maxMs: number } | null;
  shapes: number;
  decor: DecorRenderStats | null;
}

export interface MapRenderStatsWatch {
  window: RenderStatsWindow;
  longTasks: (now: number) => { count: number; totalMs: number; maxMs: number } | null;
  stop: () => void;
}

/** Chronomètre l'intervalle entre deux images de la carte, et relève les tâches longues du navigateur. */
export function watchMapRenderStats(map: maplibregl.Map): MapRenderStatsWatch {
  const window = new RenderStatsWindow(performance.now());
  let previous = 0;
  const onRender = () => {
    const now = performance.now();
    // Au-delà, la carte était au repos entre ces deux images : ce n'est pas un à-coup.
    if (previous && now - previous < IDLE_GAP_MS) window.recordFrame(now - previous, now);
    previous = now;
  };
  map.on("render", onRender);

  const tasks: { at: number; duration: number }[] = [];
  let observer: PerformanceObserver | null = null;
  if (typeof PerformanceObserver !== "undefined" && PerformanceObserver.supportedEntryTypes?.includes("longtask")) {
    observer = new PerformanceObserver((list) => {
      for (const entry of list.getEntries()) tasks.push({ at: entry.startTime + entry.duration, duration: entry.duration });
    });
    observer.observe({ type: "longtask" });
  }

  return {
    window,
    longTasks: (now) => {
      if (!observer) return null;
      while (tasks.length && tasks[0]!.at < now - RENDER_STATS_WINDOW_MS * 5) tasks.shift();
      return {
        count: tasks.length,
        totalMs: tasks.reduce((sum, task) => sum + task.duration, 0),
        maxMs: tasks.reduce((max, task) => Math.max(max, task.duration), 0),
      };
    },
    stop: () => {
      map.off("render", onRender);
      observer?.disconnect();
    },
  };
}
